import React, { createContext, useContext } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { Loader, api, Nav, NewFooter } from "../shared";
import { useMediaQuery } from "react-responsive";
import { useQuery, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AcknowledgeCommitment from "./acknowledgecommitment";
import Alliance from "./alliance";
import BatteryPoweredMembership from "./batterypoweredmembership";
import BatteryPoweredVideo from "./batterypoweredvideo";
import Customs from "./customs";
import ErrorMessage from "./errormessage";
import MembershipType from "./membershiptype";
import OpenHouse from "./openhouse";
import OpenHouseRsvp from "./openhousersvp";
import Partner from "./partner";
import PersonalInformation from "./personalinformation";
import Questions from "./questions";
import References from "./references";
import ReferencesSubmitted from "./referencessubmitted";
import Review from "./review";
import Submitted from "./submitted";
import UploadHeadshot from "./uploadheadshot";
import Community from "./community";
const queryClient = new QueryClient();

function LoginRequired({ location }) {
  if (location.pathname.includes("apply/upload-headshot/")) {
    return (
      <div>
        <h1>Thank you for starting your application!</h1>
        <h2
          style={{
            textTransform: "capitalize",
            fontSize: "12pt",
            fontFamily: "TradeGothicCondensed"
          }}
        >
          <a href="/login/?next=/apply/upload-headshot/" style={{ textDecoration: "underline" }}>
            Click here to log in and continue your application
          </a>
        </h2>
      </div>
    );
  }
  return (
    <div>
      <a href="/login/" style={{ textDecoration: "underline" }}>
        Please login to view this page
      </a>
      .
    </div>
  );
}
const authContext = createContext();
function useAuth() {
  return useContext(authContext);
}
function PrivateRoute({ children, ...rest }) {
  const auth = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        auth.person.is_authenticated ? (
          children
        ) : (
          <LoginRequired location={location}></LoginRequired>
        )
      }
    />
  );
}
function useProvideAuth({ person }) {
  return { person };
}
function ProvideAuth({ person, children }) {
  const auth = useProvideAuth({ person });
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}
function Routes({ person, mediaQuery }) {
  const total = 11;
  return (
    <ProvideAuth person={person}>
      <Router>
        <Switch>
          <Route exact path="/apply">
            <MembershipType mediaQuery={mediaQuery} person={person} />
          </Route>
          <Route exact path="/apply/alliance">
            <Alliance mediaQuery={mediaQuery} />
          </Route>
          <Route exact path="/apply/acknowledge-commitment">
            <AcknowledgeCommitment mediaQuery={mediaQuery} />
          </Route>
          <Route exact path="/apply/personal-information">
            <PersonalInformation mediaQuery={mediaQuery} page={1} total={total} />
          </Route>
          <PrivateRoute exact path="/apply/upload-headshot">
            <UploadHeadshot mediaQuery={mediaQuery} page={2} total={total} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/questions">
            <Questions mediaQuery={mediaQuery} page={3} total={total} person={person} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/customs">
            <Customs person={person} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/battery-powered-video">
            <BatteryPoweredVideo mediaQuery={mediaQuery} page={5} total={total} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/battery-powered-membership">
            <BatteryPoweredMembership mediaQuery={mediaQuery} page={6} total={total} person={person} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/references">
            <References mediaQuery={mediaQuery} page={7} total={total} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/community/">
            <Community mediaQuery={mediaQuery} page={8} total={total} person={person} />
          </PrivateRoute>
          <PrivateRoute exact path="/references">
            <ReferencesSubmitted mediaQuery={mediaQuery} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/open-house">
            <OpenHouse mediaQuery={mediaQuery} page={9} total={total} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/invite-partner/">
            <Partner mediaQuery={mediaQuery} page={10} total={total} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/review">
            <Review mediaQuery={mediaQuery} page={11} total={total} person={person} />
          </PrivateRoute>
          <PrivateRoute exact path="/apply/submitted">
            <Submitted mediaQuery={mediaQuery} />
          </PrivateRoute>
          <PrivateRoute exact path="/open-house-rsvp">
            <OpenHouseRsvp mediaQuery={mediaQuery} />
          </PrivateRoute>
        </Switch>
      </Router>
    </ProvideAuth>
  );
}

function Application() {
  const {
    isLoading,
    error,
    data: { person } = { person: { first_name: "" } }
  } = useQuery(["application"], () => api("/api/applications/v4/application/"));
  const mediaQuery = {
    isDesktopOrLaptop: useMediaQuery({ query: "(min-width: 1224px)" }),
    isBigScreen: useMediaQuery({ query: "(min-width: 1824px)" }),
    isTabletOrMobile: useMediaQuery({ query: "(max-width: 1224px)" }),
    isPortrait: useMediaQuery({ query: "(orientation: portrait)" }),
    isRetina: useMediaQuery({ query: "(min-resolution: 2dppx)" })
  };
  return (
    <>
      <Nav user={person} />
      <div style={{ minHeight: 800, paddingTop: mediaQuery.isTabletOrMobile ? 0 : 50 }}>
        <div
          style={{ margin: "60px auto", padding: 30, backgroundColor: "#fffff5", maxWidth: 1024 }}
        >
          <div
            role="main"
            style={{
              borderBottom: "1px solid #002b45",
              borderLeft: "1px solid #002b45",
              borderRight: "1px solid #002b45",
              borderTop: "1px solid #002b45",
              color: "#002b45",
              fill: "#002b45",
              paddingBottom: 30,
              paddingTop: 30,
              position: "relative",
              textAlign: "center",
              width: "100%"
            }}
          >
            {isLoading ? (
              <Loader loading={isLoading} />
            ) : (
              <Routes mediaQuery={mediaQuery} person={person} />
            )}
            {error ? <ErrorMessage error={error} /> : null}
          </div>
        </div>
      </div>
      <NewFooter />
    </>
  );
}

export default function ApplicationQueryContainer() {
  return (
    <QueryClientProvider client={queryClient}>
      <Application />
    </QueryClientProvider>
  );
}
